import { bandPower, bandpass, detrend, welchPsd } from "@musejs/signal";
//#region src/sqi.ts
/**
* Seconds of signal below which no estimate is attempted.
*
* [M] Twelve, not the four this once was. The rescaling below makes noise score
* 0 *on average*, but a short window is a single, high-variance periodogram, and
* noise then scores well above 0 by chance. Across 200 seeds of white noise at
* 64 Hz, the fraction clearing the default `minSqi` of 0.2 was 17 % at 4 s,
* 4.5 % at 8 s, and 0 at 12 s (largest 0.197), where Welch averages at least
* five 4-second segments. Below that the gate does not gate.
*/
const MINIMUM_SECONDS = 12;
/** Segment length, in seconds: resolves 0.25 Hz, enough to separate a heartbeat from breathing. */
const SEGMENT_SECONDS = 4;
/**
* How much of a signal's power sits in the cardiac band, above chance, 0…1.
*
* A quality index rather than an amplitude: an optical channel with a strong
* pulse concentrates its power around 1 Hz, while one picking up movement or
* nothing at all spreads it. Used to weight channels when fusing them, so a
* channel that is not seeing a pulse cannot drag the fused signal around.
*
* **The raw power ratio is not the score, and the difference matters.** White
* noise has a flat spectrum, so its share of the cardiac band is simply the
* ratio of the two band widths — 3.4 Hz in 7.5 Hz, or 0.45, with the defaults.
* A score that reports 0.45 for pure noise makes every threshold below it
* meaningless. So the flat-spectrum expectation is subtracted and the remainder
* rescaled: **noise scores 0, a signal entirely inside the band scores 1.**
*
* The measure is a ratio throughout, so it is unaffected by how bright the
* optode is — which matters here, because the optical scale is uncalibrated.
*/
function pulseSqi(signal, options) {
	const [pulseLow, pulseHigh] = options.pulseBand ?? [.6, 4];
	const [totalLow, totalHigh] = options.totalBand ?? [.5, 8];
	if (signal.length < options.sampleRate * MINIMUM_SECONDS) return 0;
	const segmentLength = Math.max(16, 2 ** Math.round(Math.log2(options.sampleRate * SEGMENT_SECONDS)));
	const spectrum = welchPsd(signal, {
		sampleRate: options.sampleRate,
		segmentLength
	});
	const total = bandPower(spectrum, totalLow, totalHigh);
	if (total < 1e-12) return 0;
	const share = bandPower(spectrum, pulseLow, pulseHigh) / total;
	const chance = (pulseHigh - pulseLow) / (totalHigh - totalLow);
	if (chance >= 1) return Math.min(1, share);
	return Math.max(0, Math.min(1, (share - chance) / (1 - chance)));
}
//#endregion
//#region src/bvp.ts
/**
* Fuses optical channels into one blood-volume-pulse signal.
*
* Channels are weighted by how much of their power sits in the cardiac band,
* so the ones actually seeing a pulse dominate. Each is normalised to unit
* variance first: the optical scale is uncalibrated, so raw amplitudes carry no
* comparable meaning and a brighter channel would otherwise simply win.
*
* The output is **relative**. It has no unit, and only its shape in time is
* meaningful — which is all a heart rate needs.
*/
function fuseBvp(channels, options) {
	const [low, high] = options.band ?? [.5, 8];
	const minSqi = options.minSqi ?? .2;
	const sqi = {};
	const prepared = {};
	let length = 0;
	for (const [name, raw] of Object.entries(channels)) {
		if (raw.length === 0) continue;
		length = length === 0 ? raw.length : Math.min(length, raw.length);
		const filtered = bandpass(detrend(raw), low, high, { sampleRate: options.sampleRate });
		prepared[name] = filtered;
		sqi[name] = pulseSqi(filtered, { sampleRate: options.sampleRate });
	}
	const qualified = Object.keys(prepared).filter((name) => (sqi[name] ?? 0) >= minSqi);
	const used = qualified.length > 0 ? qualified : Object.keys(prepared);
	const weights = {};
	let weightTotal = 0;
	for (const name of used) {
		const w = qualified.length > 0 ? sqi[name] : 1;
		weights[name] = w;
		weightTotal += w;
	}
	for (const name of used) weights[name] = weightTotal === 0 ? 0 : weights[name] / weightTotal;
	const bvp = new Float32Array(length);
	for (const name of used) {
		const signal = prepared[name];
		const scale = 1 / (standardDeviation(signal) || 1);
		const weight = weights[name];
		for (let i = 0; i < length; i++) bvp[i] = bvp[i] + weight * scale * signal[i];
	}
	return {
		bvp,
		sqi,
		weights,
		used
	};
}
function standardDeviation(values) {
	let mean = 0;
	for (const v of values) mean += v;
	mean /= values.length;
	let sum = 0;
	for (const v of values) sum += (v - mean) ** 2;
	return Math.sqrt(sum / values.length);
}
//#endregion
//#region src/heart-rate.ts
/**
* Finds heartbeats in a blood-volume-pulse signal.
*
* Peak detection with an adaptive threshold and a refractory period. The
* refractory period is what stops the dicrotic notch — the secondary bump in
* every arterial pulse — from being counted as a second beat, which is the
* usual way a naive peak finder reports double the true rate.
*/
function heartRate(bvp, options) {
	const [minBpm, maxBpm] = options.range ?? [36, 240];
	const refractory = Math.floor(options.sampleRate * 60 / maxBpm);
	if (bvp.length < refractory * 2) return {
		bpm: null,
		peaks: [],
		peakTimes: [],
		confidence: 0
	};
	const sorted = bvp.toSorted();
	const high = sorted[Math.floor(sorted.length * .95)];
	const median = sorted[sorted.length >> 1];
	if (!(high > median)) return {
		bpm: null,
		peaks: [],
		peakTimes: [],
		confidence: 0
	};
	const threshold = median + .4 * (high - median);
	const peaks = [];
	let last = -refractory;
	for (let i = 1; i < bvp.length - 1; i++) {
		const value = bvp[i];
		if (value < threshold) continue;
		const before = bvp[i - 1];
		const after = bvp[i + 1];
		if (value < before || value < after || value === before && value === after) continue;
		if (i - last < refractory) {
			if (peaks.length > 0 && value > bvp[peaks[peaks.length - 1]]) {
				peaks[peaks.length - 1] = i;
				last = i;
			}
			continue;
		}
		peaks.push(i);
		last = i;
	}
	const peakTimes = peaks.map((i) => (i + parabolicOffset$1(bvp, i)) / options.sampleRate);
	if (peaks.length < 3) return {
		bpm: null,
		peaks,
		peakTimes,
		confidence: 0
	};
	const intervals = [];
	for (let i = 1; i < peakTimes.length; i++) intervals.push(peakTimes[i] - peakTimes[i - 1]);
	const bpm = 60 / intervals.toSorted((a, b) => a - b)[intervals.length >> 1];
	if (bpm < minBpm || bpm > maxBpm) return {
		bpm: null,
		peaks,
		peakTimes,
		confidence: 0
	};
	const mean = intervals.reduce((a, b) => a + b, 0) / intervals.length;
	const sd = Math.sqrt(intervals.reduce((a, b) => a + (b - mean) ** 2, 0) / intervals.length);
	return {
		bpm,
		peaks,
		peakTimes,
		confidence: Math.max(0, Math.min(1, 1 - sd / mean))
	};
}
/**
* Heart-rate variability from beat times.
*
* **These are the standard measures computed from optical beat times, not from
* an ECG.** HRV is sensitive to exactly the thing a photoplethysmogram is worst
* at — locating a beat to the millisecond — and this device's timestamps carry
* 20–40 ms of uncertainty of their own. Read these as within-session trends,
* never as clinical values.
*/
function hrv(peakTimesSeconds) {
	if (peakTimesSeconds.length < 4) return null;
	const intervals = [];
	for (let i = 1; i < peakTimesSeconds.length; i++) intervals.push((peakTimesSeconds[i] - peakTimesSeconds[i - 1]) * 1e3);
	const meanRrMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
	const sdnnMs = Math.sqrt(intervals.reduce((a, b) => a + (b - meanRrMs) ** 2, 0) / intervals.length);
	let squares = 0;
	let over50 = 0;
	for (let i = 1; i < intervals.length; i++) {
		const diff = intervals[i] - intervals[i - 1];
		squares += diff * diff;
		if (Math.abs(diff) > 50) over50++;
	}
	const pairs = intervals.length - 1;
	return {
		meanRrMs,
		sdnnMs,
		rmssdMs: Math.sqrt(squares / pairs),
		pnn50: over50 / pairs,
		beats: peakTimesSeconds.length
	};
}
/**
* Offset of the true peak from sample `i`, in samples, within ±0.5.
*
* Fits a parabola through `i-1`, `i` and `i+1` and returns its vertex. Returns
* 0 where the three points are collinear and no vertex exists.
*/
function parabolicOffset$1(signal, i) {
	const y1 = signal[i - 1];
	const y2 = signal[i];
	const y3 = signal[i + 1];
	if (y1 === void 0 || y2 === void 0 || y3 === void 0) return 0;
	const denominator = y1 - 2 * y2 + y3;
	if (denominator === 0) return 0;
	const offset = .5 * (y1 - y3) / denominator;
	return Number.isFinite(offset) && Math.abs(offset) <= .5 ? offset : 0;
}
//#endregion
//#region src/respiration.ts
/**
* Estimates respiration rate from the slow modulation of an optical signal.
*
* **Feed it a raw, illuminated optical channel** — IR, NIR or red — not
* `fuseBvp`'s output and not an ambient `*_AMB` channel. `fuseBvp` band-passes at
* 0.5–8 Hz to isolate the pulse, which removes the 0.1–0.5 Hz respiratory band
* entirely. [M] Fed the fused BVP, this function once reported 22–30 breaths per
* minute on the worn captures and exactly 30.0 on the headband lying on a desk:
* 30/min is the 0.5 Hz filter edge, not breathing. An ambient channel measures
* the room's light, which can vary as slowly as breath: on the desk, `LI_AMB`
* scored 0.53 at 10.1/min.
*
* Breathing modulates peripheral blood volume, so it shows in the same optical
* channels as the pulse, two octaves lower. The estimate is the dominant
* spectral peak inside the plausible band, located between bins by parabolic
* interpolation — without it the answer can only be one of a handful of bin
* frequencies, 3.75 breaths per minute apart at 64 Hz.
*
* **Needs a long window.** At 0.1 Hz resolution a 30-second recording holds
* only three breaths, and the spectrum cannot separate breathing from drift.
* Under 30 seconds this returns `null` rather than a number that looks usable.
*/
function respiration(bvp, options) {
	const [low, high] = options.band ?? [.1, .5];
	const minimumQuality = options.minimumQuality ?? .4;
	const prepared = detrend(bvp);
	const signal = bandpass(prepared, low, high, { sampleRate: options.sampleRate });
	if (bvp.length / options.sampleRate < 30) return {
		breathsPerMinute: null,
		signal,
		quality: 0
	};
	const segmentLength = Math.min(2 ** Math.floor(Math.log2(bvp.length)), 2 ** Math.round(Math.log2(options.sampleRate * 20)));
	const spectrum = welchPsd(prepared, {
		sampleRate: options.sampleRate,
		segmentLength
	});
	const contextLow = low / 2;
	const contextHigh = high * 2;
	let peakBin = -1;
	let peakDensity = 0;
	let inBand = 0;
	let context = 0;
	for (let bin = 0; bin < spectrum.frequencies.length; bin++) {
		const frequency = spectrum.frequencies[bin];
		const density = spectrum.density[bin];
		if (frequency >= contextLow && frequency <= contextHigh) context += density;
		if (frequency < low || frequency > high) continue;
		inBand += density;
		if (density > peakDensity) {
			peakDensity = density;
			peakBin = bin;
		}
	}
	const share = context === 0 ? 0 : inBand / context;
	const chance = (high - low) / (contextHigh - contextLow);
	const quality = Math.max(0, Math.min(1, (share - chance) / (1 - chance)));
	if (peakBin < 0 || quality < minimumQuality) return {
		breathsPerMinute: null,
		signal,
		quality
	};
	if (!isInteriorPeak(spectrum.frequencies, spectrum.density, peakBin, low, high)) return {
		breathsPerMinute: null,
		signal,
		quality
	};
	return {
		breathsPerMinute: (spectrum.frequencies[peakBin] + parabolicOffset(spectrum.density, peakBin) * spectrum.resolutionHz) * 60,
		signal,
		quality
	};
}
/** Whether bin `i` is a strict local maximum with both neighbours inside `[low, high]`. */
function isInteriorPeak(frequencies, density, i, low, high) {
	const below = frequencies[i - 1];
	const above = frequencies[i + 1];
	if (below === void 0 || above === void 0 || below < low || above > high) return false;
	const peak = density[i];
	return density[i - 1] < peak && density[i + 1] < peak;
}
/** Offset of the true peak from bin `i`, in bins, from a parabola through it and its neighbours. */
function parabolicOffset(values, i) {
	const left = values[i - 1];
	const centre = values[i];
	const right = values[i + 1];
	if (left === void 0 || right === void 0) return 0;
	const denominator = left - 2 * centre + right;
	if (denominator === 0) return 0;
	return Math.max(-.5, Math.min(.5, .5 * (left - right) / denominator));
}
//#endregion
export { fuseBvp, heartRate, hrv, pulseSqi, respiration };

//# sourceMappingURL=index.js.map